import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, createBlock as _createBlock, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "header" }
const _hoisted_2 = { class: "logo" }
const _hoisted_3 = {
  key: 0,
  class: "body"
}
const _hoisted_4 = { class: "input-section" }
const _hoisted_5 = { class: "input-wrapper" }
const _hoisted_6 = {
  key: 0,
  class: "valid"
}
const _hoisted_7 = {
  key: 1,
  class: "selection-content"
}
const _hoisted_8 = {
  key: 2,
  class: "form-content"
}
const _hoisted_9 = { class: "options" }
const _hoisted_10 = { class: "fields" }
const _hoisted_11 = { class: "fields-row" }
const _hoisted_12 = { class: "date-wrapper" }
const _hoisted_13 = {
  key: 0,
  class: "error"
}
const _hoisted_14 = {
  key: 0,
  class: "footer"
}
const _hoisted_15 = {
  key: 1,
  class: "footer"
}
const _hoisted_16 = {
  key: 2,
  class: "footer"
}

import { ref, computed, watch } from "vue";
import {
  Modal,
  Button,
  Input,
  Icon,
  Select,
  Checkbox,
  DateSelector,
  Stepper,
} from "@/components/ui";
import { Field, defineRule, useForm } from "vee-validate";
import { TaskType } from "@/utils/enums";
import { getTaskTypeIcon } from "@/utils/iconMapping";
import { useBucketOptions } from "@/composables/bucket/useBucketOptions";
import {
  browseLinkedInSearchPage,
  startLinkedInImportJob,
} from "@/composables/contacts/useLinkedInSearch";
import { useAppStore } from "@/composables/app/useAppStore";
import { useBillingStatus, USAGE_BLOCKED_TOOLTIP } from "@/composables/billing/useBillingStatus";
import { useToast } from "@/composables/ui/useToast";
import ContactsFoundCard from "./ContactsFoundCard.vue";
import ContactSelectionStep from "./ContactSelectionStep.vue";
import LinkedInWarningModal from "./LinkedInWarningModal.vue";
import LinkedInConfirmationModal from "./LinkedInConfirmationModal.vue";
import { useQueryClient } from "@tanstack/vue-query";
import type { LinkedInSearchProspect } from "@/dtos/Unipile.dto";

const PAGE_SIZE = 50;

type Step = "url" | "selection" | "form";

export default /*@__PURE__*/_defineComponent({
  __name: 'LinkedInImportModal',
  props: {
  show: {
    type: Boolean,
    default: false,
  },
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

defineRule("required", (value: unknown, [fieldName = "Field"]: [string?]) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return `${fieldName} is required`;
  }
  return true;
});

const { validate } = useForm();

const props = __props;

const emit = __emit;

const queryClient = useQueryClient();

const { account } = useAppStore();
const organizationId = computed(() => account.value?.organizationId);

const { showUsageBasedFeaturesModalOnce, isUsageBlocked } = useBillingStatus();

const currentStep = ref<Step>("url");
const stepLabels = ["Enter URL", "Choose Contacts", "Set Options"];
const currentStepIndex = computed(() => {
  const stepMap: Record<Step, number> = { url: 0, selection: 1, form: 2 };
  return stepMap[currentStep.value];
});

const modalSize = computed(() => {
  if (currentStep.value === "selection") return "xl";
  return "md";
});

const salesNavigatorUrl = ref("");
const urlError = ref("");
const isUrlValid = ref(false);
const isSearching = ref(false);
const isPaging = ref(false);
const contactsCount = ref<number>(0);
const showConfirmation = ref(false);
const showWarning = ref(false);
const estimatedContacts = ref<number>(0);
const isSubmitting = ref(false);

const sessionId = ref<string | null>(null);
const pageContacts = ref<LinkedInSearchProspect[]>([]);
const totalCount = ref<number | null>(null);
const totalItems = ref<number>(0);
const currentPageIndex = ref<number>(0);
const perPage = ref<number>(PAGE_SIZE);
const hasMore = ref<boolean>(false);
const capReached = ref<boolean>(false);

const canGoNext = computed(() => {
  if (hasMore.value) return true;
  const next = (currentPageIndex.value + 1) * perPage.value;
  return next < totalItems.value;
});

const selectedContactIds = ref<string[]>([]);

const $toast = useToast();

const formData = ref({
  enrichEmail: true,
  enrichPhoneNumber: true,
  taskType: null as string | null,
  bucketOptionId: null as string | null,
  followUpDate: null as string | null,
});

const bucketOptionsParams = computed(() => {
  if (!organizationId.value) return null;
  return { organizationId: organizationId.value };
});

const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

const bucketSelectOptions = computed(() => {
  const options = bucketOptionsData.value || [];
  return options.map((bucket: any) => ({
    value: bucket.id,
    label: bucket.name,
    icon: "fa6-solid:bucket",
    iconColor: bucket.colorHex,
  }));
});

const taskTypeOptions = computed(() => {
  return Object.values(TaskType).map((task) => ({
    value: task,
    label: task.charAt(0).toUpperCase() + task.slice(1),
    icon: getTaskTypeIcon(task),
  }));
});

const validateUrl = () => {
  const url = salesNavigatorUrl.value.trim();
  urlError.value = "";

  if (!url) {
    isUrlValid.value = false;
    return;
  }

  const salesNavigatorPattern = /^https?:\/\/(www\.)?linkedin\.com\/sales\/search\/people/;

  if (!salesNavigatorPattern.test(url)) {
    urlError.value = "Please enter a valid LinkedIn Sales Navigator URL";
    isUrlValid.value = false;
    return;
  }

  isUrlValid.value = true;
  urlError.value = "";
};

const handleSearch = async () => {
  if (!isUrlValid.value) {
    validateUrl();
    return;
  }

  isSearching.value = true;
  try {
    const data = await browseLinkedInSearchPage({
      url: salesNavigatorUrl.value.trim(),
      limit: perPage.value,
      page: 0,
    });
    sessionId.value = data.sessionId;
    pageContacts.value = data.items;
    totalCount.value = data.totalCount;
    totalItems.value = data.totalItems;
    currentPageIndex.value = data.page;
    perPage.value = data.perPage;
    hasMore.value = data.hasMore;
    capReached.value = data.capReached;

    selectedContactIds.value = [];

    if ((totalCount.value ?? 0) > 100) {
      estimatedContacts.value = totalCount.value ?? data.items.length;
      showWarning.value = true;
      isSearching.value = false;
      return;
    }

    currentStep.value = "selection";
  } catch (error: any) {
    console.error("Error searching contacts:", error);
    urlError.value =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to search contacts. Please try again.";
    isUrlValid.value = false;
  } finally {
    isSearching.value = false;
  }
};

const proceedWithSearch = () => {
  showWarning.value = false;
  if (sessionId.value) {
    currentStep.value = "selection";
  }
};

const fetchPage = async (targetPage: number, targetPerPage: number) => {
  if (!sessionId.value || isPaging.value) return;
  isPaging.value = true;
  try {
    const data = await browseLinkedInSearchPage({
      url: salesNavigatorUrl.value.trim(),
      sessionId: sessionId.value,
      page: Math.max(0, targetPage),
      limit: targetPerPage,
    });
    pageContacts.value = data.items;
    totalCount.value = data.totalCount ?? totalCount.value;
    totalItems.value = data.totalItems;
    currentPageIndex.value = data.page;
    perPage.value = data.perPage;
    hasMore.value = data.hasMore;
    capReached.value = data.capReached;
  } catch (error: any) {
    $toast?.error(error?.message || "Failed to load page.");
  } finally {
    isPaging.value = false;
  }
};

const loadNextPage = () => fetchPage(currentPageIndex.value + 1, perPage.value);
const loadPrevPage = () => fetchPage(currentPageIndex.value - 1, perPage.value);

const handlePerPageChange = (newPerPage: number) => {
  if (newPerPage === perPage.value) return;
  return fetchPage(0, newPerPage);
};

const handleBackToUrl = () => {
  currentStep.value = "url";
  resetSelectionState();
};

const handleContinueToForm = () => {
  if (selectedContactIds.value.length === 0) return;
  contactsCount.value = selectedContactIds.value.length;
  currentStep.value = "form";
};

const handleBackToSelection = () => {
  currentStep.value = "selection";
  formData.value = {
    enrichEmail: true,
    enrichPhoneNumber: true,
    taskType: null,
    bucketOptionId: null,
    followUpDate: null,
  };
};

const handleEnrichAndImport = async () => {
  const result = await validate();
  if (!result.valid) return;

  showConfirmation.value = true;
};

const confirmEnrichAndImport = async () => {
  if (!sessionId.value) {
    $toast?.error("Search session expired. Please re-run the search.");
    return;
  }

  await showUsageBasedFeaturesModalOnce("linkedin_import", async () => {
    isSubmitting.value = true;

    try {
      const result = await startLinkedInImportJob({
        sessionId: sessionId.value!,
        selectedProspectIds: selectedContactIds.value,
        taskType: formData.value.taskType ?? undefined,
        bucketOptionId: formData.value.bucketOptionId ?? undefined,
        followUpDate: formData.value.followUpDate ?? undefined,
        enrichEmail: formData.value.enrichEmail,
        enrichPhoneNumber: formData.value.enrichPhoneNumber,
      });

      $toast?.info(
        `Importing ${result.total} contact${result.total !== 1 ? "s" : ""}. We'll notify you when it's complete.`
      );

      queryClient.invalidateQueries({ queryKey: ["notifications"] });

      showConfirmation.value = false;
      handleClose();
    } catch (error: any) {
      console.error("Error enriching contacts:", error);
      $toast?.error(error?.message || "Failed to start import. Please try again.");
    } finally {
      isSubmitting.value = false;
    }
  });
};

const resetSelectionState = () => {
  sessionId.value = null;
  pageContacts.value = [];
  totalCount.value = null;
  totalItems.value = 0;
  currentPageIndex.value = 0;
  perPage.value = PAGE_SIZE;
  hasMore.value = false;
  capReached.value = false;
  selectedContactIds.value = [];
};

const handleClose = () => {
  salesNavigatorUrl.value = "";
  urlError.value = "";
  isUrlValid.value = false;
  currentStep.value = "url";
  contactsCount.value = 0;
  showConfirmation.value = false;
  showWarning.value = false;
  estimatedContacts.value = 0;
  resetSelectionState();
  formData.value = {
    enrichEmail: true,
    enrichPhoneNumber: true,
    taskType: null,
    bucketOptionId: null,
    followUpDate: null,
  };
  emit("close");
};

watch(
  () => props.show,
  (newVal) => {
    if (!newVal) {
      handleClose();
    }
  }
);

return (_ctx: any,_cache: any) => {
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Modal), {
      show: __props.show,
      size: modalSize.value,
      onClose: handleClose,
      class: "modal"
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_unref(Icon), {
              icon: "mdi:linkedin",
              class: "logo-icon"
            })
          ]),
          _cache[13] || (_cache[13] = _createElementVNode("h2", { class: "title" }, "Import from LinkedIn Sales Navigator", -1))
        ])
      ]),
      footer: _withCtx(() => [
        (currentStep.value === 'url')
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(_unref(Button), {
                variant: "primary",
                size: "lg",
                icon: "mdi:magnify",
                class: "search-btn",
                disabled: !isUrlValid.value || isSearching.value,
                loading: isSearching.value,
                onClick: handleSearch,
                block: ""
              }, {
                default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                  _createTextVNode(" Search Contacts ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled", "loading"])
            ]))
          : (currentStep.value === 'selection')
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _createVNode(_unref(Button), {
                  variant: "secondary",
                  size: "md",
                  onClick: handleBackToUrl
                }, {
                  default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                    _createTextVNode("Back", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_unref(Button), {
                  variant: "primary",
                  size: "md",
                  disabled: selectedContactIds.value.length === 0,
                  onClick: handleContinueToForm
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(" Continue with " + _toDisplayString(selectedContactIds.value.length) + " Contact" + _toDisplayString(selectedContactIds.value.length !== 1 ? "s" : ""), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]))
            : (currentStep.value === 'form')
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createVNode(_unref(Button), {
                    variant: "secondary",
                    size: "md",
                    onClick: handleBackToSelection
                  }, {
                    default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                      _createTextVNode("Back", -1)
                    ]))]),
                    _: 1
                  }),
                  _withDirectives((_openBlock(), _createBlock(_unref(Button), {
                    variant: "primary",
                    size: "md",
                    icon: "mdi:linkedin",
                    disabled: _unref(isUsageBlocked),
                    onClick: handleEnrichAndImport
                  }, {
                    default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                      _createTextVNode(" Enrich & Import Contacts ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"])), [
                    [_directive_tooltip, {
            content: _unref(USAGE_BLOCKED_TOOLTIP),
            disabled: !_unref(isUsageBlocked),
          }]
                  ])
                ]))
              : _createCommentVNode("", true)
      ]),
      default: _withCtx(() => [
        _createVNode(_unref(Stepper), {
          steps: stepLabels,
          "current-step": currentStepIndex.value,
          class: "modal-stepper"
        }, null, 8, ["current-step"]),
        (currentStep.value === 'url')
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _cache[14] || (_cache[14] = _createElementVNode("label", { class: "label" }, "Paste your LinkedIn Sales Navigator search URL to import and enrich contacts.", -1)),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Input), {
                    modelValue: salesNavigatorUrl.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((salesNavigatorUrl).value = $event)),
                    placeholder: "https://www.linkedin.com/sales/search/people?...",
                    size: "md",
                    error: urlError.value,
                    onInput: validateUrl
                  }, null, 8, ["modelValue", "error"]),
                  (isUrlValid.value && salesNavigatorUrl.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:check-circle",
                          class: "valid-icon"
                        })
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _cache[15] || (_cache[15] = _createElementVNode("p", { class: "hint" }, "Copy the URL from your LinkedIn Sales Navigator search results page", -1))
              ])
            ]))
          : (currentStep.value === 'selection')
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(ContactSelectionStep, {
                  contacts: pageContacts.value,
                  "selected-ids": selectedContactIds.value,
                  "total-count": totalCount.value,
                  "total-items": totalItems.value,
                  "current-page": currentPageIndex.value,
                  "per-page": perPage.value,
                  "can-go-next": canGoNext.value,
                  "can-go-prev": currentPageIndex.value > 0,
                  loading: isPaging.value,
                  "cap-reached": capReached.value,
                  "onUpdate:selectedIds": _cache[1] || (_cache[1] = ($event: any) => (selectedContactIds.value = $event)),
                  "onUpdate:perPage": handlePerPageChange,
                  onNextPage: loadNextPage,
                  onPrevPage: loadPrevPage
                }, null, 8, ["contacts", "selected-ids", "total-count", "total-items", "current-page", "per-page", "can-go-next", "can-go-prev", "loading", "cap-reached"])
              ]))
            : (currentStep.value === 'form')
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(ContactsFoundCard, { "contacts-count": contactsCount.value }, null, 8, ["contacts-count"]),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", {
                      class: "option",
                      onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (formData.value.enrichEmail = !formData.value.enrichEmail), ["stop"]))
                    }, [
                      _createVNode(_unref(Checkbox), {
                        modelValue: formData.value.enrichEmail,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.enrichEmail) = $event)),
                        size: "md",
                        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                      }, null, 8, ["modelValue"]),
                      _cache[16] || (_cache[16] = _createElementVNode("span", { class: "option-label" }, "Enrich Email", -1))
                    ]),
                    _createElementVNode("div", {
                      class: "option",
                      onClick: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (formData.value.enrichPhoneNumber = !formData.value.enrichPhoneNumber), ["stop"]))
                    }, [
                      _createVNode(_unref(Checkbox), {
                        modelValue: formData.value.enrichPhoneNumber,
                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.enrichPhoneNumber) = $event)),
                        size: "md",
                        onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"]))
                      }, null, 8, ["modelValue"]),
                      _cache[17] || (_cache[17] = _createElementVNode("span", { class: "option-label" }, "Enrich Phone Number", -1))
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, [
                      _createVNode(_unref(Field), {
                        id: "taskType",
                        modelValue: formData.value.taskType,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.value.taskType) = $event)),
                        rules: { required: ['Task Type'] },
                        name: "taskType"
                      }, {
                        default: _withCtx(({ field, errorMessage }) => [
                          _createVNode(_unref(Select), {
                            "model-value": field.value,
                            "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                            onBlur: field.onBlur,
                            label: "Task Type",
                            options: taskTypeOptions.value,
                            placeholder: "Select task type",
                            required: "",
                            error: errorMessage,
                            size: "md"
                          }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options", "error"])
                        ]),
                        _: 1
                      }, 8, ["modelValue"]),
                      _createVNode(_unref(Field), {
                        id: "bucket",
                        modelValue: formData.value.bucketOptionId,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formData.value.bucketOptionId) = $event)),
                        rules: { required: ['Bucket'] },
                        name: "bucket"
                      }, {
                        default: _withCtx(({ field, errorMessage }) => [
                          _createVNode(_unref(Select), {
                            "model-value": field.value,
                            "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                            onBlur: field.onBlur,
                            label: "Bucket",
                            options: bucketSelectOptions.value,
                            placeholder: "Select bucket",
                            required: "",
                            error: errorMessage,
                            size: "md"
                          }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur", "options", "error"])
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]),
                    _createVNode(_unref(Field), {
                      id: "followUpDate",
                      modelValue: formData.value.followUpDate,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((formData.value.followUpDate) = $event)),
                      rules: { required: ['Follow up date'] },
                      name: "followUpDate"
                    }, {
                      default: _withCtx(({ field, errorMessage }) => [
                        _createElementVNode("div", _hoisted_12, [
                          _createVNode(_unref(DateSelector), {
                            "model-value": field.value,
                            "onUpdate:modelValue": ($event: any) => (field.onChange($event)),
                            onBlur: field.onBlur,
                            label: "Follow up date",
                            placeholder: "Select Date",
                            "disable-past": false,
                            required: ""
                          }, null, 8, ["model-value", "onUpdate:modelValue", "onBlur"]),
                          errorMessage
                            ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(errorMessage), 1))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 1
                    }, 8, ["modelValue"])
                  ])
                ]))
              : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["show", "size"]),
    _createVNode(LinkedInWarningModal, {
      show: showWarning.value,
      "estimated-contacts": estimatedContacts.value,
      loading: isSearching.value,
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showWarning.value = false)),
      onConfirm: proceedWithSearch
    }, null, 8, ["show", "estimated-contacts", "loading"]),
    _createVNode(LinkedInConfirmationModal, {
      show: showConfirmation.value,
      "contacts-count": contactsCount.value,
      loading: isSubmitting.value,
      onClose: _cache[12] || (_cache[12] = ($event: any) => (showConfirmation.value = false)),
      onConfirm: confirmEnrichAndImport
    }, null, 8, ["show", "contacts-count", "loading"])
  ], 64))
}
}

})